/**
 * Questionnaire page - A page that prompts the user to answer questions.
 *
 * This page is used to collect user information.
 *
 * Created by Ron on 2024/11/26.
 * Copyright (c) 2024-present iWoo Ltd. All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "../BecomeMemberPage/Header/Header";
import Footer from "../BecomeMemberPage/Footer/Footer";
import DoBPicker from "../UserInfoModal/DoBPicker/DoBPicker";
import SetGender from "../UserInfoModal/SetGender/SetGender";
import PreferredWorkoutPicker from "../UserInfoModal/PreferredWorkout/PreferredWorkout";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import CloseIcon from "../../icons/CloseIcon";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

const QuestionnairePage = () => {
  const history = useHistory();
  const location = useLocation();
  const { theme } = useTheme();

  const {
    user,
    hasTriedToAutoLogin,
    isLoggedIn,
    questionnaire,
    getQuestionnaire,
    updateCustomerInfo,
    questionnaireState,
    setQuestionnaireState,
    // userDoB,
    gender,
    setGender,
    //userPreferredWorkouts,
    completeQuestionnaire,
  } = useAppState();

  const [genderQuestions, setGenderQuestions] = useState();
  const [preferredWorkoutQuestions, setPreferredWorkoutQuestions] = useState();

  useEffect(() => {
    getQuestionnaire();

    if (user && user.birthday) {
      setQuestionnaireState("gender");
    }
  }, []);

  useEffect(() => {
    if (!questionnaire) {
      return;
    }

    setGenderQuestions(getSingleQuestions(questionnaire));
    setPreferredWorkoutQuestions(getMultipleQuestions(questionnaire));
  }, [questionnaire]);

  const handleSubmit = (val) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get("return_to");

    if (user && val.date) {
      updateCustomerInfo(
        { birthday: new Date(val.date).toISOString() },
        user.id
      ).then(() => setQuestionnaireState("gender"));
    } else if (user && val.preferredWorkouts) {
      const params = {
        customerId: user.id,
        answers: [
          {
            questionId: genderQuestions.id,
            answerIds: [gender],
          },
          {
            questionId: preferredWorkoutQuestions.id,
            answerIds: [...val.preferredWorkouts],
          },
        ],
      };
      completeQuestionnaire(params).then(
        () => user.email && history.push(returnTo ? returnTo : "/")
      );
    } else if (val.gender) {
      setGender(val.gender);
      setQuestionnaireState("preferredWorkout");
    }
  };

  const handleQuestionnaireSkip = (val) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get("return_to");

    const returnUrl = returnTo ? `${decodeURIComponent(returnTo)}` : "/";

    if (user && val.preferredWorkouts) {
      const params = {
        customerId: user.id,
        answers: [
          {
            questionId: genderQuestions.id,
            answerIds: [gender],
          },
          {
            questionId: preferredWorkoutQuestions.id,
            answerIds: [...val.preferredWorkouts],
          },
        ],
      };
      completeQuestionnaire(params).then(
        () => user.email && history.push(returnUrl)
      );
    } else if (user && val.gender) {
      const params = {
        customerId: user.id,
        answers: [
          {
            questionId: genderQuestions.id,
            answerIds: [val.gender],
          },
        ],
      };
      completeQuestionnaire(params)
        .then(() => user.email && history.push(returnUrl))
        .finally(() => setQuestionnaireState("dob"));
    } else {
      // const params = {
      //   customerId: user.id,
      //   answers: [],
      // };
      // completeQuestionnaire(params)
      //   .then(() => user.email && history.push("/"))
      //   .finally(() => setQuestionnaireState("dob"));
      setQuestionnaireState("dob");
      history.push(returnUrl);
    }
  };

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content="TruBe connects you with the best fitness & wellbeing trainers, specialising in one-to-one sessions for Pilates, Yoga, Personal Training, HIIT, Running, and more"
      />
      <Header
        behaviour="none"
        isLoggedIn={!!(user && user.id)}
        // heroRef={heroRef}
        showLinks={false}
        showMenu={false}
      />
      <div className="tbk-flex tbk-min-h-screen tbk-flex-col tbk-items-center tbk-pt-5 tbk-pb-8 md:tbk-justify-center md:tbk-pt-6">
        <div className={"tbk-relative tbk-w-full tbk-p-3 sm:tbk-w-120"}>
          {questionnaireState === "gender" ? (
            <SetGender
              questions={genderQuestions}
              onNext={handleSubmit}
              onSkip={handleQuestionnaireSkip}
            />
          ) : questionnaireState === "preferredWorkout" ? (
            <PreferredWorkoutPicker
              questions={getMultipleQuestions(questionnaire)}
              onNext={handleSubmit}
              onSkip={handleQuestionnaireSkip}
            />
          ) : (
            <DoBPicker
              questions={genderQuestions}
              onNext={handleSubmit}
              onSkip={handleQuestionnaireSkip}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

QuestionnairePage.displayName = "QuestionnairePage";

const getSingleQuestions = (questions) => {
  if (!questions || !questions.length) {
    return;
  }
  return questions.find((val) => val.type === "SINGLE");
};

const getMultipleQuestions = (questions) => {
  if (!questions || !questions.length) {
    return;
  }
  return questions.find((val) => val.type === "MULTIPLE");
};

export default QuestionnairePage;
